import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import type { SerializedEditorState } from 'lexical';
import type { Dispatch, SetStateAction } from 'react';
import { Text } from '../../../components/ui/text.tsx';
import { cn } from '../../../lib/utils.ts';
import type { AIActionType } from '../hooks/use-improve-writing.ts';
import type { EntityType } from '../types/entity-types.ts';
import { type AIWritingState, Lexical } from './lexical.tsx';

export type RichTextValue = SerializedEditorState;

export type RichTextInputProps = {
    disableVoiceRecording?: boolean;
    autoFocus?: boolean;
    entityId?: string;
    /** Entity type for improve-writing hook - enables AI actions when 'note' */
    entityType?: EntityType;
    value: RichTextValue | null | undefined;
    onChange: (value: RichTextValue) => void;
    placeholder?: string;
    /** Current AI writing state */
    aiWritingState?: AIWritingState;
    /** Callback when AI writing state changes */
    setAIWritingState?: Dispatch<SetStateAction<AIWritingState>>;
    onAIInteraction?: (action: AIActionType, sessionId: string) => void;
    className?: string;
    error?: boolean;
};

export const RichTextInput: React.FC<RichTextInputProps> = ({
    disableVoiceRecording,
    autoFocus = false,
    entityId,
    entityType,
    value,
    onChange,
    placeholder,
    aiWritingState,
    setAIWritingState,
    onAIInteraction,
    className,
    error = false,
}) => {
    return (
        <div
            className={cn(
                'border w-full p-2 px-3 rounded-lg bg-white relative focus-within:border-blue-600',
                error && 'border-red-500 focus-within:border-red-600',
                className,
            )}
        >
            <Lexical
                disableVoiceRecording={disableVoiceRecording}
                autoFocus={autoFocus}
                entityId={entityId}
                entityType={entityType}
                content={value}
                onChange={onChange}
                editable
                aiWritingState={aiWritingState}
                setAIWritingState={setAIWritingState}
                onAIInteraction={onAIInteraction}
                contentEditable={
                    <ContentEditable
                        className="relative outline-hidden text-base sm:text-sm text-neutral-950 min-h-16 max-h-[60vh] overflow-auto"
                        aria-placeholder={placeholder ?? ''}
                        placeholder={
                            <Text color="neutral-600" className="absolute top-2 left-3 pointer-events-none">
                                {placeholder}
                            </Text>
                        }
                        style={{ userSelect: 'text' }}
                    />
                }
            />
        </div>
    );
};
